import { endOfDay } from 'date-fns';
import { Dictionary, groupBy } from 'lodash';

import { coerceToDate } from '../dates/coerceToDate';
import { toMMDD, toYYYY } from '../dates/formats';

export type AppointmentTypes =
  | 'bcm_evaluation'
  | 'bcm_session'
  | 'coach_evaluation'
  | 'coach_session'
  | 'therapist_evaluation'
  | 'therapist_session'
  | 'psychometrist_evaluation'
  | 'psychometrist_session'
  | 'psychometristassistant_session'
  | 'neuropsychologist_session';

export const IntakeAppointmentTypes = ['bcm_evaluation', 'psychometrist_evaluation'];

export const CoreAppointmentTypes = [
  'bcm_evaluation',
  'bcm_session',
  'coach_evaluation',
  'coach_session',
  'therapist_evaluation',
  'therapist_session',
  'prescriber_evaluation',
  'prescriber_session',
  'nurse_session',
];

export const NpAppointmentTypes = [
  'psychometrist_evaluation',
  'psychometrist_session',
  'psychometristassistant_session',
  'neuropsychologist_session',
];

export enum AppointmentStatus {
  Unscheduled = 'unscheduled',
  Scheduled = 'scheduled',
  Completed = 'completed',
  Cancelled = 'cancelled',
  NoReschedule = 'no_reschedule',
  PatientNoShow = 'patient_no_show',
  ProviderNoShow = 'provider_no_show',
  Blocked = 'blocked',
}

export interface Appointment {
  id: string;
  taskId?: string | null;
  memberPlanId?: string;
  providerAccountId?: string | null;
  memberId: string;
  name: string;
  description: string;
  status: AppointmentStatus;
  caregiverJoinUrl?: string | null;
  virtualMeetingInfo?: string | null;
  scheduledStartTime: string | null;
  duration: number;
  scheduleBeforeDate: string;
  cancellationDate?: string | null;
  cancellationReason?: string | null;
  async: boolean;
  createdAt: string;
  updatedAt: string;
  appointmentType: AppointmentTypes;
  schedulingCycle: 'initial';
  scheduleAfterDate: string;
  credentialedName: string | null;
  avatarUrlSmall: string | null;
  avatarUrlDefault: string | null;
  caregiverOnly: boolean;
  timeZone?: string;
  returning?: boolean;
  minimumWaitHours: number;
  skippable: boolean;
  cancelable: boolean;
  reschedulable: boolean;
  bookable: boolean;
}

export const appointmentMissedOrCancelled = (a: Appointment) => {
  return (
    a.status === AppointmentStatus.Cancelled ||
    a.status === AppointmentStatus.PatientNoShow ||
    a.status === AppointmentStatus.ProviderNoShow
  );
};

export interface GroupedAppointments {
  groups: Dictionary<Appointment[]>;
  sortedKeys: string[];
}

export interface AppointmentListVM {
  past: GroupedAppointments;
  upcoming: GroupedAppointments | null;
}

export const groupAppointments = (appts: Appointment[]): AppointmentListVM => {
  const groups = groupBy(
    appts,
    (a) => `${toYYYY(a.scheduleAfterDate)}: ${toMMDD(a.scheduleAfterDate)} - ${toMMDD(a.scheduleBeforeDate)}`,
  );

  Object.values(groups).forEach((appts) => {
    appts.sort((a, b) => {
      if (!a.scheduledStartTime) return 1;
      if (!b.scheduledStartTime) return -1;

      return a.scheduledStartTime < b.scheduledStartTime ? -1 : 1;
    });
  });

  const sortedKeys = Object.keys(groups).sort((a, b) => {
    const dateA = new Date(groups[a][0].scheduleAfterDate);
    const dateB = new Date(groups[b][0].scheduleAfterDate);

    return dateA < dateB ? -1 : 1;
  });

  const now = new Date();
  const indexOfFuture = sortedKeys.findIndex((key) => {
    const date = endOfDay(coerceToDate(groups[key][0].scheduleBeforeDate));
    if (date < now) return false;
    return true;
  });

  if (indexOfFuture === -1) {
    return {
      upcoming: null,
      past: { groups, sortedKeys: sortedKeys.reverse() },
    };
  }

  const past = sortedKeys.slice(0, indexOfFuture).reverse();
  const upcoming = sortedKeys.slice(indexOfFuture);

  return {
    past: keysToGroups(past, groups),
    upcoming: keysToGroups(upcoming, groups),
  };
};

const keysToGroups = (keys: string[], groups: Dictionary<Appointment[]>) => {
  const ret: GroupedAppointments = {
    sortedKeys: keys,
    groups: {},
  };

  keys.forEach((k) => {
    ret.groups[k] = groups[k];
  });
  return ret;
};
